<script lang="ts">
  import ModalDialog from "../../../overlay/_modules/modal-dialog/index"
  import Icon from "../../../primitives/_modules/icon/index"
  import SafeHtml from "../../../security/_modules/safe-html/index"
  import { HelpTopics } from "./_modules/help-topics/index"
  import type {
    HelpDialogConfig,
    HelpDialogContext,
    HelpDialogItem,
    HelpDialogImage,
    HelpDialogSection,
  } from "../../../../core/help/types"

  const {
    open = false,
    onClose = () => {},
    config,
    context = {},
  } = $props<{
    open?: boolean
    onClose?: () => void
    config?: HelpDialogConfig
    context?: HelpDialogContext
  }>()

  const whenOk = (item: HelpDialogItem, ctx: HelpDialogContext) => {
    if (!item.when) return true

    try {
      return item.when(ctx)
    } catch (err) {
      console.warn("help item when() failed", err)

      return false
    }
  }

  // Help illustrations are app-hosted assets, never arbitrary HTML or external requests.
  const visibleImage = (image?: HelpDialogImage): HelpDialogImage | undefined => {
    if (typeof image?.src !== "string" || typeof image.alt !== "string" || !image.alt.trim()) {
      return undefined
    }

    const src = image.src.trim()

    if (!src.startsWith("/") || src.startsWith("//") || /[\\\u0000-\u0020]/.test(src)) {
      return undefined
    }

    return { ...image, src }
  }

  const normalizeSections = (sections: HelpDialogSection[] = [], ctx: HelpDialogContext = {}) =>
    sections
      .map((section, id) => {
        const items = (section.items ?? []).filter((item) => whenOk(item, ctx))
        const image = visibleImage(section.image)

        return { ...section, id, items, image }
      })
      .filter((section) => section.items.length > 0 || section.image)

  const sections = $derived(normalizeSections(config?.sections, context))
  const size = $derived(config?.size === "xlarge" ? "xl" : (config?.size ?? "large"))
</script>

{#snippet lead()}
  {#if config?.lead}
    <div class="help-dialog__lead"><SafeHtml html={config.lead} /></div>
  {/if}
{/snippet}

{#if open}
  {#key config}
    <HelpTopics {sections}>
      {#snippet children({ navigation, content, contentKey })}
        <ModalDialog
          title={config?.title ?? "Hilfe"}
          appearance="detail"
          {size}
          {contentKey}
          {onClose}
          navigationSnippet={sections.length > 1 ? navigation : undefined}
          descriptionSnippet={config?.lead ? lead : undefined}
        >
          <div class="help-dialog">
            {#if config?.infoStrip}
              <div class="help-dialog__notice">
                {#if config.infoStrip.icon}
                  <span aria-hidden="true"
                    ><Icon path={config.infoStrip.icon} size="1.05rem" /></span
                  >
                {/if}
                <div><SafeHtml html={config.infoStrip.text} /></div>
              </div>
            {/if}
            {#if sections.length}
              {@render content()}
            {:else}
              <p class="help-dialog__empty">Keine zusätzlichen Hinweise vorhanden.</p>
            {/if}
          </div>
        </ModalDialog>
      {/snippet}
    </HelpTopics>
  {/key}
{/if}

<style lang="less">
  .help-dialog {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    min-width: 0;
    color: var(--text-invers-100);
    line-height: 1.6;
    overflow-wrap: anywhere;
  }

  .help-dialog__lead {
    color: inherit;
    font-size: inherit;
  }

  .help-dialog__notice {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.65rem 0.85rem;
    border-inline-start: 2px solid var(--text-invers-200);
    background: color-mix(in srgb, var(--text-invers-100) 5%, transparent);
    font-size: 0.85rem;

    > span {
      display: flex;
      flex: 0 0 auto;
      margin-top: 0.15rem;
    }
  }

  .help-dialog__lead :global(p) {
    color: inherit;
  }

  .help-dialog__lead :global(p),
  .help-dialog__notice :global(p),
  .help-dialog__empty {
    margin: 0;
  }
</style>
